import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import type { JobArchitecture, JobArchitectureSummary } from '~/shared/data-contract/job-architecture';

import type { ApplicationFetchers } from '../../fetchers/create-application-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { isQuerySuccess } from './is-success-query';
import { jobArchitectureKeys } from './query-keys';

const REFETCH_INTERVAL_MS = 2000;

const createQueryFn =
    (fetchJobArchitecture: ApplicationFetchers['fetchJobArchitecture']) =>
    (jobArchitectureId: string) =>
    async ({ signal }: { signal: AbortSignal }): Promise<JobArchitecture> => {
        const result = await fetchJobArchitecture(jobArchitectureId, { signal });

        if (result.isOk === true) {
            return result.value;
        }

        throw result.error;
    };

export const useJobArchitecture = (jobArchitectureId: string | undefined): UseQueryResult<JobArchitecture, Error> => {
    const { fetchJobArchitecture } = useApplicationFetchers();
    const queryClient = useQueryClient();

    const query = useQuery({
        enabled: !is.undefined(jobArchitectureId),
        queryFn: createQueryFn(fetchJobArchitecture)(jobArchitectureId!),
        queryKey: jobArchitectureKeys.architectureDetail(jobArchitectureId!),
        refetchInterval: (query) => {
            if (!isQuerySuccess(query)) {
                return false;
            }

            const isGenerating = query.state.data.generationInfo?.inProgress === true;

            return isGenerating ? REFETCH_INTERVAL_MS : false;
        },
        staleTime: 10_000,
        throwOnError: false,
    });

    useEffect(() => {
        const data = query.data;
        if (is.undefined(data)) return;

        for (const key of [jobArchitectureKeys.architectures(), jobArchitectureKeys.architecturesWithStatistics()]) {
            queryClient.setQueryData(key, (old: readonly JobArchitectureSummary[] | undefined) =>
                old?.map((architecture) => (architecture.id === data.id ? data : architecture)),
            );
        }
    }, [query.data, queryClient]);

    return query;
};
